<script setup lang="ts">
    import { getTranslatedProperty } from '@shopware/helpers';
    import type { Schemas } from '#shopware';

    const {
        address,
        canSetDefault = true,
        canEdit = true,
        canDelete = false
    } = defineProps<{
        address: Schemas['CustomerAddress'];
        canSetDefault?: boolean;
        canEdit?: boolean;
        canDelete?: boolean;
    }>();
    const emits = defineEmits<{
        (e: 'success'): void;
    }>();
    const { hfpPushSuccess, hfpPushError } = useNotificationHelpers();
    const {
        setDefaultCustomerShippingAddress,
        setDefaultCustomerBillingAddress,
        deleteCustomerAddress,
        loadCustomerAddresses
    } = useAddress();
    const { defaultBillingAddressId, defaultShippingAddressId } = useShopwareUser();
    const { refreshSessionContext } = useSessionContext();
    const { t } = useI18n();
    const { auth } = useLoginState();

    const canBeDeleted = computed(
        () => canDelete && defaultShippingAddressId.value !== address.id && defaultBillingAddressId.value !== address.id
    );

    const setDefaultShippingAddress = async () => {
        try {
            await setDefaultCustomerShippingAddress(address.id);
            refreshSessionContext();
            hfpPushSuccess(t('account.messages.defaultShippingAddressSuccess'));
        } catch (_error) {
            hfpPushError(t('account.messages.defaultShippingAddressError'));
        }
    };

    const setDefaultBillingAddress = async () => {
        try {
            await setDefaultCustomerBillingAddress(address.id);
            await refreshSessionContext();
            hfpPushSuccess(t('account.messages.defaultBillingAddressSuccess'));
        } catch (_error) {
            hfpPushError(t('account.messages.defaultBillingAddressError'));
        }
    };

    const removeAddress = async (addressId: string) => {
        try {
            await deleteCustomerAddress(addressId);
            hfpPushSuccess(t('account.messages.addressDeletedSuccess'));
        } catch (_error) {
            hfpPushError(t('account.messages.addressDeletedError'));
        } finally {
            await loadCustomerAddresses();
        }
    };

    const addAddressModalController = useModal();
    const addressSaved = () => {
        addAddressModalController.close();
        emits('success');
    };
</script>

<template>
    <div class="col-span-6 lg:col-span-3 max-w-md">
        <div class="address-header">
            <span
                class="text-xl leading-none text-secondary-900 mr-2"
                data-testid="address-box-name"
            >
                {{ `${address.firstName} ${address.lastName}` }}
            </span>

            <button
                v-if="canEdit && !auth"
                title="Open address editor"
                class="cursor-pointer i-carbon-edit text-xl inline-block"
                data-testid="address-edit"
                @click.prevent="addAddressModalController.open"
            />
            <SharedModal
                v-if="!auth"
                :controller="addAddressModalController"
            >
                <SharedAccountAddressForm
                    :address="address"
                    @success="addressSaved"
                />
            </SharedModal>
            <div
                v-if="canBeDeleted && !auth"
                class="i-carbon-delete text-xl inline-block cursor-pointer ml-2"
                data-testid="address-delete"
                @click.prevent="removeAddress(address.id)"
            />
        </div>
        <div class="flow-root">
            <p v-if="address.additionalAddressLine1">
                {{ address.additionalAddressLine1 }}
            </p>
            <p>
                <span data-testid="address-box-street">
                    {{ address.street }}
                </span>
            </p>
            <p>
                <span data-testid="address-box-zipcode">{{ address.zipcode }}</span>
                <span>&nbsp;</span>
                <span data-testid="address-box-city">{{ address.city }}</span>
            </p>
            <p
                v-if="address.country"
                class="text-sm"
            >
                <span>({{ getTranslatedProperty(address.country, 'name') }}</span>
                <span v-if="address.countryState">, {{ getTranslatedProperty(address.countryState, 'name') }}</span
                >)
            </p>
        </div>
        <div v-if="canSetDefault">
            <a
                v-if="defaultShippingAddressId !== address.id"
                role="button"
                tabindex="0"
                class="block text-sm mt-4 font-medium text-blue-600 hover:underline"
                data-testid="address-set-default-shipping"
                @click="setDefaultShippingAddress()"
            >
                {{ t('account.setDefaultShippingAddress') }}
            </a>
            <a
                v-if="defaultBillingAddressId !== address.id"
                role="button"
                tabindex="0"
                class="block text-sm mt-2 font-medium text-blue-600 hover:underline"
                data-testid="address-set-default-billing"
                @click="setDefaultBillingAddress()"
            >
                {{ t('account.setDefaultBillingAddress') }}
            </a>
        </div>
    </div>
</template>

<style scoped>
    @import '~/assets/scss/checkout/account-address-card.scss';
</style>
